import Timer from '@wetransfer/concorde-timer';
import Browser from '@wetransfer/concorde-browser';
import get from 'lodash/get';

import { TransferAPI, finalizeTransfer } from '../services/api/transfer';
import { ActionHelper } from '../../core-bundle/store/actions-generator';
import { TransferConfig } from '../config/transfer-config';
import { TRANSFER_STATES } from '../constant/transfer-states';
import {
  canFilesBeAdded,
  hasStarted,
  validateTransfer,
} from '../helpers/index';
import { ChunksQueue } from '../services/queues/chunks-queue';
import { processChunkTask } from '../services/multipart-uploader/upload-chunk';
import { uploadFile } from '../services/multipart-uploader/upload-file';
import TransferItem from '../models/transfer-item';
import { getCreateTransferRequestParams } from '../helpers/get-create-transfer-request-params.js';
import { TRANSFER_TYPES } from '../constant/transfer-types';

import { prepareTransferDownload } from './download-actions';

import ErrorHandler from '@module/error-handler';
import Metric from '@module/metric';
import TransferActions, {
  actions as TransferActionTypes,
  dispatchNotification,
  getUserType,
} from '@app/store/action/transfer';
import Analytics from '@module/analytics';
import LaunchDarkly from '@module/launchdarkly';
import Timers from '@module/timers';
import NotificationActions, {
  actions as NotificationActionTypes,
} from '@app/store/action/notification';
import { getSubNodesFromItemName } from '@app/helper/transfer-items';
import TransferError from '@app/helper/error/index';
import TransferErrorMessages from '@app/helper/error/messages';
import { isLoggedInPlusUser } from '@app/helper/user';
import {
  getCurrentSize,
  getIsForwardedTransfer,
} from '@app/transfer-bundle/store/selectors';
import I18n from '@module/i18n';
import Helper from '@app/helper';
import Logger from '@module/logger';
import { HttpResponseCodes } from '@app/modules/http/axios/response';
import { FF_REMOVE_TRANSFER_DEFAULT_EXPIRY } from '@app/constant/experiments';

let chunksQueue = null;

export const TransferUploadActionTypes = ActionHelper.generateTypes([
  'RESET_UPLOAD_PROGRESS',
  'UPDATE_UPLOAD_TIME',
  'SET_LAST_UPDATE',
  'UPDATE_UPLOAD_PROGRESS',
]);

export const TransferUploadActions = ActionHelper.generateActions({
  resetUploadProgress: [TransferUploadActionTypes.RESET_UPLOAD_PROGRESS],
  updateUploadTime: [
    TransferUploadActionTypes.UPDATE_UPLOAD_TIME,
    'bytesSentSoFar',
    'currentSize',
  ],
  setLastUpdate: [TransferUploadActionTypes.SET_LAST_UPDATE],
  updateUploadProgress: [
    TransferUploadActionTypes.UPDATE_UPLOAD_PROGRESS,
    'bytesSentSoFar',
    'currentSize',
    'transferUploadStartedAt',
  ],
  pauseUpload,
  resumeUpload,
  cancelUpload,
  resetTransfer,
  abortTransfer,
  pauseUploadQueue,
  resumeUploadQueue,
  stopUploadProgress,
  startUploadProgress,
  pauseUploadProgress,
  estimateUploadTime,
  calculateUploadProgress,
  verifyPassword,
  abortUploadOnRefresh,
  createTransfer,
  processUpload,
  startUpload,
});

let remainingTimer;
let progressTimer;

function estimateUploadTime(transferFiles, currentSize) {
  return (dispatch) => {
    dispatch(TransferUploadActions.setLastUpdate());

    if (remainingTimer) {
      remainingTimer.stop();
    }

    remainingTimer = new Timer(
      TransferConfig.upload.remainingTimeInterval,
      () => {
        // We use the transfer files inside the action as the files hold a
        // reference to the bytes sent, and we need to calculate the
        // whole amount of bytes sent every time this action runs.
        const bytesSentSoFar = transferFiles.reduce(
          (count, file) => count + file.bytesSent,
          0
        );

        dispatch(
          TransferUploadActions.updateUploadTime(bytesSentSoFar, currentSize)
        );

        dispatch(
          TransferUploadActions.estimateUploadTime(transferFiles, currentSize)
        );
      }
    );
  };
}

function stopUploadProgress() {
  return (dispatch) => {
    dispatch(TransferUploadActions.pauseUploadProgress());
    dispatch(TransferUploadActions.resetUploadProgress());
  };
}

function calculateUploadProgress(
  transferFiles,
  currentSize,
  transferUploadStartedAt
) {
  return (dispatch) => {
    // We use the transfer files inside the action as the files hold a
    // reference to the bytes sent, and we need to calculate the
    // whole amount of bytes sent every time this action runs.
    const bytesSentSoFar = transferFiles.reduce(
      (count, file) => count + file.bytesSent,
      0
    );

    dispatch(
      TransferUploadActions.updateUploadProgress(
        bytesSentSoFar,
        currentSize,
        transferUploadStartedAt
      )
    );

    if (progressTimer) {
      progressTimer.stop();
    }

    progressTimer = new Timer(TransferConfig.upload.progressInterval, () => {
      dispatch(
        TransferUploadActions.calculateUploadProgress(
          transferFiles,
          currentSize,
          transferUploadStartedAt
        )
      );
    });
  };
}

function startUploadProgress(
  transferFiles,
  currentSize,
  transferUploadStartedAt
) {
  return (dispatch) => {
    dispatch(
      TransferUploadActions.estimateUploadTime(transferFiles, currentSize)
    );
    dispatch(
      TransferUploadActions.calculateUploadProgress(
        transferFiles,
        currentSize,
        transferUploadStartedAt
      )
    );
  };
}

function pauseUploadProgress() {
  return () => {
    if (remainingTimer) {
      remainingTimer.stop();
      remainingTimer = null;
    }

    if (progressTimer) {
      progressTimer.stop();
      progressTimer = null;
    }
  };
}

function pauseUploadQueue() {
  return () => {
    if (chunksQueue) {
      chunksQueue.pause();
    }
  };
}

function resumeUploadQueue() {
  return () => {
    if (chunksQueue) {
      chunksQueue.resume();
    }
  };
}

function abortUploadQueue() {
  return () => {
    if (chunksQueue) {
      chunksQueue.abort();
    }
  };
}

export function pauseUpload() {
  return (dispatch) => {
    dispatch(pauseUploadQueue());

    dispatch(TransferUploadActions.pauseUploadProgress());

    dispatch({
      type: TransferActionTypes.TRANSFER_SET_STATE,
      state: TRANSFER_STATES.PAUSED,
    });
  };
}

export function resumeUpload(transfer) {
  return (dispatch) => {
    dispatch(resumeUploadQueue());

    dispatch(
      TransferUploadActions.startUploadProgress(
        transfer.files,
        getCurrentSize({ transfer }),
        transfer.uploadStartedAt
      )
    );

    dispatch({
      type: TransferActionTypes.TRANSFER_SET_STATE,
      state: TRANSFER_STATES.STARTED,
    });
  };
}

function verifyPassword(transferId, secret, recipient, password) {
  return async (dispatch) => {
    try {
      const fetchedTransfer = await TransferAPI.fetchTransfer(
        transferId,
        secret,
        recipient,
        password,
        false
      );

      dispatch(TransferActions.verifiedPassword(password));
      dispatch(prepareTransferDownload(fetchedTransfer, password));
      dispatch(NotificationActions.clearTransfer());
    } catch (error) {
      dispatch(NotificationActions.pushTransfer({ text: 'InvalidPassword' }));
    }
  };
}

export function abortUploadOnRefresh(transfer) {
  return async () => {
    if (
      transfer.id &&
      (transfer.state === TRANSFER_STATES.STARTED ||
        transfer.state === TRANSFER_STATES.PAUSED)
    ) {
      await TransferAPI.exitTransfer(transfer.id);
    }
  };
}

export function cancelUpload(transfer) {
  return (dispatch) => {
    dispatch(abortUploadQueue());

    if (transfer.id) {
      TransferAPI.cancelTransfer(transfer.id);
    }

    transfer.files.forEach((file) => file.reset());

    dispatch(TransferUploadActions.resetUploadProgress());
    dispatch({ type: TransferActionTypes.UPLOAD_RESET });
  };
}

function abortTransfer(transfer) {
  return (dispatch) => {
    dispatch(cancelUpload(transfer));
    dispatch({
      type: TransferActionTypes.TRANSFER_SET_STATE,
      state: TRANSFER_STATES.ABORTED,
    });
  };
}

function resetTransfer() {
  return (dispatch) => {
    dispatch({ type: TransferActionTypes.TRANSFER_RESET });
  };
}

export function createTransfer({ transfer, isForwardedTransfer }) {
  return async (dispatch) => {
    try {
      const transferDetails = await TransferAPI.createTransferWithType(
        transfer.type,
        getCreateTransferRequestParams(transfer)
      );

      if (!transferDetails || !transferDetails.id) {
        throw new TransferError('InvalidTransferId');
      }

      if (isForwardedTransfer) {
        // Rather than normalizing the transfer, we are using the
        // expiry_in_seconds property directly from the API response,
        // hence the different naming (instead of expiryInSeconds)
        dispatch({
          type: TransferActionTypes.TRANSFER_SET_EXPIRY_IN_SECONDS,
          expiryInSeconds: transferDetails.expiry_in_seconds,
        });

        dispatch({
          type: TransferActionTypes.TRANSFER_SET_SHORT_URL,
          shortURL: transferDetails.shortened_url,
        });
      }

      dispatch({
        type: TransferActionTypes.TRANSFER_SET_ID,
        id: transferDetails.id,
      });

      if (transferDetails.display_name) {
        dispatch({
          type: TransferActionTypes.TRANSFER_SET_DISPLAY_NAME,
          displayName: transferDetails.display_name,
        });
      }

      if (transferDetails.message === 'email_verification_required') {
        dispatch({
          type: TransferActionTypes.UPLOAD_NEED_VERIFICATION,
        });
      }
    } catch (error) {
      dispatch({
        type: TransferActionTypes.TRANSFER_SET_STATE,
        state: TRANSFER_STATES.ABORTED,
      });

      ErrorHandler.track(error);

      const isSpecificError = [
        TransferErrorMessages.LIMIT_REACHED,
        TransferErrorMessages.USER_BLOCKED,
        TransferErrorMessages.UNAVAILABLE_PARENT,
        TransferErrorMessages.HUMAN_VERIFICATION_REQUIRED,
        TransferErrorMessages.TOO_MANY_RECIPIENTS,
      ].includes(error.message);

      if (isSpecificError) {
        throw error;
      }

      throw new TransferError('TransferFailed', { transferId: transfer.id });
    }
  };
}

export function uploadTransfer({
  channel,
  account,
  user,
  session,
  allowedSize,
  isForwardedTransfer,
  currentSize,
}) {
  return async (dispatch, getState) => {
    dispatch({ type: TransferActionTypes.UPLOAD_PENDING });
    dispatch({
      type: TransferActionTypes.TRANSFER_SET_LANGUAGE,
      language: session.language,
    });
    dispatch({
      type: TransferActionTypes.TRANSFER_SET_DOMAIN_USER_ID,
      domainUserId: session.domainUserId,
    });
    // Set the transfer channel if logged in + has subdomain but not ON a channel
    // Which is almost impossible, besides after just changing your subdomain
    const isChannel = channel && channel.name;
    const hasSubdomain = account && account.subdomain_name;
    if (!isChannel && user.loggedIn && hasSubdomain) {
      dispatch({
        type: TransferActionTypes.TRANSFER_SET_CHANNEL,
        channel: account.subdomain_name,
      });
    }

    try {
      validateTransfer({
        transfer: getState().transfer,
        maximumNumberOfRecipients: user.rules.maximumNumberOfRecipients,
        maximumNumberOfFiles: user.rules.maximumNumberOfFiles,
        allowedSize,
        currentSize,
      });
    } catch (error) {
      dispatchNotification(
        dispatch,
        getState().transfer,
        channel,
        error.message
      );
      throw error;
    }

    try {
      await dispatch(
        TransferUploadActions.createTransfer({
          transfer: getState().transfer,
          isForwardedTransfer,
        })
      );

      if (!getState().transfer.verification_required) {
        await dispatch(
          TransferUploadActions.startUpload({
            transfer: getState().transfer,
            channel,
            currentSize,
            isForwardedTransfer,
          })
        );
      }
    } catch (error) {
      const errorMessage = error.message;

      if (errorMessage === TransferErrorMessages.HUMAN_VERIFICATION_REQUIRED) {
        dispatch({
          type: TransferActionTypes.RECAPTCHA_CHALLENGE_RECEIVED,
          transfer_id: error.transferId,
        });
      } else {
        dispatch({
          type: TransferActionTypes.UPLOAD_ERROR,
          error: errorMessage,
        });
        dispatchNotification(
          dispatch,
          getState().transfer,
          channel,
          errorMessage
        );
      }
    }
  };
}

export function createForward({ transferToForward, allowedSize, currentSize }) {
  return async (dispatch, getState) => {
    dispatch(abortUploadQueue());

    dispatch(TransferUploadActions.stopUploadProgress());

    let transferType = transferToForward.type;
    let sender = transferToForward.sender;

    // If it's a link we'll just switch back to e-mail
    // and use last sender from cookie.
    if (transferType === TRANSFER_TYPES.LINK) {
      sender = Helper.getLastUsedSender();
      transferType = TRANSFER_TYPES.EMAIL;
    }

    let state = getState();

    // Sometimes we still haven't filled in a sender
    // even though we DO have one. Let's use that!
    if (state.transfer.sender && !sender) {
      sender = state.transfer.sender;
    }

    let expiry = transferToForward.expiry;

    const defaultExpiry = get(
      state.user,
      'preferences.transfer_default_expiry_string'
    );

    if (
      LaunchDarkly.getVariant(FF_REMOVE_TRANSFER_DEFAULT_EXPIRY) &&
      isLoggedInPlusUser(state.user)
    ) {
      expiry = state.user.rules.defaultProExpiry;
    } else if (defaultExpiry) {
      expiry = defaultExpiry;
    }

    await dispatch(
      TransferActions.init({
        userType: getUserType({
          user: state.user,
          channel: state.channel,
          transferFromState: state.transfer,
        }),
        expiry,
        defaultProExpiry: state.user.rules.defaultProExpiry,
        defaultFreeExpiry: state.user.rules.defaultFreeExpiry,
        transferType,
        accountId: state.account.id,
        channelName: transferToForward.channel,
        message: transferToForward.message,
        parentId: transferToForward.id,
        sender,
        recipients: Helper.isDefaultRecipientInUploadLocked({
          channelDefaultRecipient: state.channel.defaultRecipient,
          channelProfileReceivingLocked: state.channel.profileReceivingLocked,
          userLoggedIn: state.user.loggedIn,
          channelName: state.channel.name,
          accountSubDomainName: state.account.subdomain_name,
        })
          ? state.transfer.recipients
          : [],
      })
    );

    state = getState();

    dispatch(
      addFiles({
        transfer: state.transfer,
        files: transferToForward.files,
        allowedSize,
        currentSize,
      })
    );
  };
}

export function addFiles({
  transfer,
  files,
  allowedSize,
  currentSize,
  maximumNumberOfFiles,
  shouldThrowError = false,
}) {
  return async (dispatch, getState) => {
    try {
      if (hasStarted(transfer.state, transfer.id)) {
        throw new Error('TransferStarted');
      }

      if (!files) {
        throw new Error('MissingFiles');
      }

      let withDuplicates = false;
      // We use `Array.prototype.reduce.call` as `files` array will not always
      // be an instance of `Array`. In the case of IE, it is a `FileList`.
      const validatedFiles = Array.prototype.reduce.call(
        files,
        (result, file) => {
          const transferItem = new TransferItem(file);

          const topLevelItemName = getSubNodesFromItemName(
            // Relative path only exists if item was dropped into the window.
            // In the case when it was selected through the input control,
            // it will only have `name` property.
            file.relativePath || file.name
          ).shift();
          if (
            transfer.itemsTree.items.find(
              (item) => item.name === topLevelItemName
            )
          ) {
            withDuplicates = true;
            return result;
          }

          return [...result, transferItem];
        },
        []
      );

      const state = getState();
      const isForwardedTransfer = getIsForwardedTransfer(state);

      if (
        !canFilesBeAdded({
          currentSize,
          files: validatedFiles,
          allowedSize,
          isForwardedTransfer,
        })
      ) {
        throw new Error('TooManyBytes');
      }

      const totalFileCount = validatedFiles.length + transfer.files.length;
      if (totalFileCount > maximumNumberOfFiles) {
        throw new Error('TooManyFiles');
      }

      dispatch({
        type: TransferActionTypes.TRANSFER_ADD_FILES,
        files: validatedFiles,
      });

      // Only now throw error because we chose to allow non-duplicate filenames
      // to still be added to the list, instead of just blocking everything on error
      if (withDuplicates) {
        throw new Error('DuplicateFilename');
      }
    } catch (error) {
      ErrorHandler.track(error);

      dispatch({
        type: NotificationActionTypes.NOTIFICATION_TRANSFER_SET,
        text: error.message,
      });

      if (shouldThrowError) {
        throw error;
      }
    }
  };
}

export function validateRecaptcha({
  token,
  transfer,
  channel,
  isForwardedTransfer,
  currentSize,
}) {
  return async (dispatch) => {
    try {
      if (!transfer.id) {
        throw new TransferError(TransferErrorMessages.RECAPTCHA_FAILED);
      }

      await dispatch({
        type: TransferActionTypes.TRANSFER_SET_STATE,
        state: TRANSFER_STATES.INIT,
      });

      await TransferAPI.validateRecaptcha(transfer.id, {
        token,
        expire_after_option: transfer.expiry,
      });

      Metric.send('human_verification_succeeded');
      await dispatch(
        TransferUploadActions.startUpload({
          transfer,
          channel,
          currentSize,
          isForwardedTransfer,
        })
      );
    } catch (error) {
      if (error.message === TransferErrorMessages.RECAPTCHA_FAILED) {
        await dispatch({
          type: TransferActionTypes.TRANSFER_SET_STATE,
          state: TRANSFER_STATES.RECAPTCHA,
        });
      }

      dispatchNotification(dispatch, transfer, channel, error.message);
    }
  };
}

export function verify({ token, isForwardedTransfer, email }) {
  return async (dispatch, getState) => {
    const { transfer, channel } = getState();

    try {
      await TransferAPI.verifyToken(transfer.id, {
        code: token,
        expire_after_option: transfer.expiry,
      });
      dispatch({ type: TransferActionTypes.UPLOAD_VERIFIED });
      dispatch(
        TransferUploadActions.startUpload({
          transfer,
          channel,
          currentSize: transfer.size,
          isForwardedTransfer,
        })
      );
    } catch (error) {
      switch (error.status) {
        case HttpResponseCodes.TOO_MANY_REQUESTS: {
          dispatch(
            NotificationActions.pushTransfer({
              text: 'TransferEmailVerificationThrottleError',
              button: {
                text: I18n.t(
                  'transfer.email-verification.throttle-error-action'
                ),
                action() {
                  dispatch(TransferActions.setState(TRANSFER_STATES.INIT));
                  dispatch(TransferActions.resetVerificationRequired());
                  dispatch({
                    type: TransferActionTypes.TRANSFER_SET_ID,
                    id: false,
                  });
                },
              },
            })
          );
          throw error;
        }
        default: {
          dispatch(
            NotificationActions.pushTransfer({
              text: 'TransferEmailVerificationError',
              button: {
                text: I18n.t(
                  'transfer.email-verification.error-expiry-time-action'
                ),
                action() {
                  dispatch(resendVerifyEmail(email));
                },
              },
            })
          );
          throw error;
        }
      }
    }
  };
}

export function resendVerifyEmail(email) {
  return async (dispatch, getState) => {
    const { transfer } = getState();

    try {
      await TransferAPI.resendToken(transfer.id, {
        sender_email: email,
      });
      dispatch({ type: TransferActionTypes.TRANSFER_SET_SENDER, email });
      dispatch({
        type: TransferActionTypes.TRANSFER_SET_STATE,
        state: TRANSFER_STATES.VERIFY,
      });
    } catch (error) {
      switch (error.status) {
        case HttpResponseCodes.TOO_MANY_REQUESTS:
          dispatch(
            NotificationActions.pushTransfer({
              text: 'TransferEmailVerificationResendThrottleError',
              button: {
                text: I18n.t(
                  'transfer.email-verification-resend.throttle-error-action'
                ),
                action() {
                  dispatch(TransferActions.setState(TRANSFER_STATES.INIT));
                  dispatch(TransferActions.resetVerificationRequired());
                  dispatch({
                    type: TransferActionTypes.TRANSFER_SET_ID,
                    id: false,
                  });
                },
              },
            })
          );
          throw error;
        default:
          dispatch(
            NotificationActions.pushTransfer({
              text: 'TransferEmailVerificationResendError',
            })
          );
          throw error;
      }
    }
  };
}

export function processUpload({ transfer, isForwardedTransfer }) {
  return async (dispatch, getState) => {
    let transferDetails;

    dispatch({
      type: TransferActionTypes.TRANSFER_SET_STATE,
      state: TRANSFER_STATES.STARTED,
    });

    dispatch({
      type: TransferActionTypes.TRANSFER_SET_UPLOAD_STARTED_AT,
      uploadStartedAt: Date.now(),
    });

    if (!isForwardedTransfer) {
      try {
        await uploadFiles(transfer);
      } catch (error) {
        Logger.error(error);
        ErrorHandler.addMetadata('transfer_id', transfer.id);
        ErrorHandler.track(error);

        dispatch(TransferUploadActions.abortTransfer());

        throw new TransferError(TransferErrorMessages.TRANSFER_FAILED);
      }

      try {
        if (!transfer.id) {
          throw new TransferError(TransferErrorMessages.MISSING_TRANSFER_ID);
        }
        transferDetails = await finalizeTransfer(transfer.id);
      } catch (error) {
        ErrorHandler.addMetadata('transfer_id', transfer.id);
        ErrorHandler.track(error);

        dispatch({
          type: TransferActionTypes.TRANSFER_SET_STATE,
          state: TRANSFER_STATES.ABORTED,
        });

        throw new TransferError(TransferErrorMessages.TRANSFER_FAILED);
      }

      // Rather than normalizing the transfer, we are using the
      // expiry_in_seconds property directly from the API response,
      // hence the different naming (instead of expiryInSeconds)
      dispatch({
        type: TransferActionTypes.TRANSFER_SET_EXPIRY_IN_SECONDS,
        expiryInSeconds: transferDetails.expiry_in_seconds,
      });

      dispatch({
        type: TransferActionTypes.TRANSFER_SET_SHORT_URL,
        shortURL: transferDetails.shortened_url,
      });

      dispatch({
        type: TransferActionTypes.TRANSFER_SET_SECURITY_HASH,
        securityHash: transferDetails.security_hash,
      });
    }

    dispatch({
      type: TransferActionTypes.TRANSFER_SET_STATE,
      state: TRANSFER_STATES.FINISHED,
    });

    dispatch({
      type: TransferActionTypes.UPLOAD_DONE,
      transfer: {
        ...getState().transfer,
        state: TRANSFER_STATES.PROCESSING,
      },
    });
  };
}

async function uploadFiles(transfer) {
  chunksQueue = new ChunksQueue({ processChunkTask });

  for (const item of transfer.files) {
    // If item is not a file, aka, is an empty directory, don't try to upload
    if (!item.isFile) continue;

    item.id = await TransferAPI.fetchFileObjectId(transfer.id, {
      name: item.name,
      size: item.size,
    });

    await uploadFile(transfer.id, item, chunksQueue);
  }
}

export function startUpload({
  transfer,
  channel,
  isForwardedTransfer,
  currentSize,
}) {
  return async (dispatch) => {
    const transferEventData = makeTransferEventData(
      transfer,
      'upload',
      isForwardedTransfer,
      currentSize
    );
    // Is it the first time a user is uploading?
    if (Helper.getWTSent() === 0) {
      // then record the days since first upload
      transferEventData.days_since_first_upload = Helper.getDaysSinceFirstVisit();
    }

    Analytics.trackGoogleEvent({
      category: 'Transfer',
      action: 'Upload',
      label: transfer.type,
      value: currentSize,
      transfer: transferEventData,
      user: {
        download_count: Helper.getWTDownloaded(),
        upload_count: Helper.getWTSent(),
        first_visit_date: Helper.getFirstVisitDate(),
      },
    });

    Timers.start('transfer.upload');

    // Start the Transfer, monitor changes and also
    // catch errors if they occur.
    try {
      dispatch(
        TransferUploadActions.startUploadProgress(
          transfer.files,
          currentSize,
          transfer.uploadStartedAt
        )
      );

      dispatch({ type: TransferActionTypes.UPLOAD_ERROR_RESET });

      // Upload actual transfer
      await dispatch(
        TransferUploadActions.processUpload({
          transfer,
          isForwardedTransfer,
        })
      );

      Helper.incrementWTSent();

      transferEventData.success = true;
      transferEventData.cancelled = false;
      transferEventData.duration = Timers.stop('transfer.upload');
    } catch (error) {
      dispatch({ type: TransferActionTypes.UPLOAD_ERROR, error });
      dispatchNotification(dispatch, transfer, channel, error);
    } finally {
      dispatch(TransferUploadActions.stopUploadProgress());

      dispatch({ type: TransferActionTypes.UPLOAD_RESET });
    }
  };
}

function makeTransferEventData(
  transfer,
  mode,
  isForwardedTransfer,
  currentSize
) {
  return {
    size: currentSize,
    forward: isForwardedTransfer,
    password: !!transfer.password,
    expiry: transfer.expiry,
    file_types: Array.from(new Set(transfer.items.map((f) => f.type))),
    duration: undefined,
    success: undefined,
    mode,
    type: transfer.type,
    item_count: transfer.items.length,
    app_type: Browser.isMobile ? 'mobile' : 'web',
    receiver_count: transfer.recipients.length,
  };
}
